import { useState, useMemo, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Search, Check, X, Plus } from "lucide-react";
import ScrollArea from "../ui/shared/ScrollArea";
import { useTags } from "@/hooks/useTags";
import { normalizeTagName } from "@/lib/tagName";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface Tag {
  id: string;
  account_id: string;
  name: string;
  color: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface TagSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableTags: Tag[];
  selectedTags: string[];
  onApplyTags: (tags: string[]) => Promise<void>;
  loading?: boolean;
  onTagCreated?: (newTag: Tag) => void;
}

export const TagSelectorModal = ({
  open,
  onOpenChange,
  availableTags,
  selectedTags,
  onApplyTags,
  loading = false,
  onTagCreated
}: TagSelectorModalProps) => {
  const { t } = useTranslation("chat");
  const { createTag } = useTags();
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSelectedTags, setTempSelectedTags] = useState<string[]>([]);
  const [applying, setApplying] = useState(false);
  const [creatingTag, setCreatingTag] = useState(false);
  const [localTags, setLocalTags] = useState<Tag[]>([]);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (open && !wasOpen) {
      setTempSelectedTags(selectedTags);
      setSearchQuery("");
      setLocalTags(availableTags);
    }
  }, [open, selectedTags, availableTags]);

  const allTags = useMemo(() => {
    return [...localTags];
  }, [localTags]);

  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) {
      return allTags.filter(tag => tag.is_active);
    }

    const query = searchQuery.toLowerCase().trim();
    return allTags.filter(
      tag => tag.is_active && tag.name.toLowerCase().includes(query)
    );
  }, [allTags, searchQuery]);

  const canCreateNewTag = useMemo(() => {
    if (!searchQuery.trim() || creatingTag) return false;

    const normalizedSearch = normalizeTagName(searchQuery);
    const tagExists = allTags.some(
      tag => normalizeTagName(tag.name) === normalizedSearch
    );

    return !tagExists && normalizedSearch.length > 0 && normalizedSearch.length <= 50;
  }, [searchQuery, allTags, creatingTag]);

  const handleToggleTag = (tagName: string) => {
    setTempSelectedTags(prev => {
      if (prev.includes(tagName)) {
        return prev.filter(t => t !== tagName);
      } else {
        return [...prev, tagName];
      }
    });
  };

  const handleCreateTag = async () => {
    if (!canCreateNewTag) return;

    setCreatingTag(true);
    try {
      const tagName = normalizeTagName(searchQuery);
      const newTag = await createTag({
        name: tagName,
        color: '#3B82F6',
        is_active: true
      });

      if (newTag) {
        setLocalTags(prev => (prev.some(tag => tag.id === newTag.id) ? prev : [newTag, ...prev]));

        if (onTagCreated) {
          onTagCreated(newTag);
        }

        const name = normalizeTagName(newTag.name);
        setTempSelectedTags(prev => (prev.includes(name) ? prev : [...prev, name]));
        setSearchQuery("");

        toast.success(t("tagModal.created", { name }));
      } else {
        toast.error(t("tagModal.createError"));
      }
    } catch (error: any) {
      console.error('Erro ao criar tag:', error);

      if (error.message?.includes('unique') || error.message?.includes('duplicate')) {
        toast.error(t("tagModal.duplicate"));
      } else {
        toast.error(t("tagModal.createError"));
      }
    } finally {
      setCreatingTag(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      await onApplyTags(tempSelectedTags);
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao aplicar tags:', error);
    } finally {
      setApplying(false);
    }
  };

  const handleCancel = () => {
    setTempSelectedTags(selectedTags);
    setSearchQuery("");
    onOpenChange(false);
  };

  const changesCount = tempSelectedTags.filter(t => !selectedTags.includes(t)).length +
    selectedTags.filter(t => !tempSelectedTags.includes(t)).length;

  const hasChanges = changesCount > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border p-0 gap-0 max-w-[500px] max-h-[80vh] flex flex-col dark:bg-neutral-900 dark:border-neutral-700">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border dark:border-neutral-800">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <DialogTitle className="text-foreground text-xl dark:text-white">{t("tagModal.title")}</DialogTitle>
              <p className="text-sm text-muted-foreground dark:text-neutral-400">
                {filteredTags.length} {filteredTags.length === 1 ? 'tag disponível' : 'tags disponíveis'}
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-hover rounded-lg transition-colors duration-200 dark:hover:bg-neutral-800"
              aria-label={t("tagModal.close")}
            >
              <X className="w-5 h-5 text-muted-foreground dark:text-neutral-400" />
            </button>
          </div>
        </DialogHeader>

        <div className="px-6 py-4 border-b border-border dark:border-neutral-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground dark:text-neutral-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("tagModal.search")}
              className="pl-10 bg-input border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 dark:bg-neutral-800 dark:border-neutral-700 dark:text-white dark:placeholder:text-neutral-500 dark:focus:border-blue-500 dark:focus:ring-blue-500/20"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-hover rounded transition-colors duration-200 dark:hover:bg-neutral-700"
              >
                <X className="w-3 h-3 text-muted-foreground dark:text-neutral-400" />
              </button>
            )}
          </div>
        </div>

        <ScrollArea
          classNames={{
            root: "flex-1 px-6 py-4 min-h-0"
          }}
        >
          {loading ? (
            <div className="flex flex-col gap-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-14 bg-card-muted dark:bg-neutral-800/50 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {canCreateNewTag && (
                <button
                  onClick={handleCreateTag}
                  disabled={creatingTag}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border border-dashed border-green-500/50 bg-green-500/10 hover:bg-green-500/20 transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-center w-5 h-5 rounded bg-green-500">
                    {creatingTag ? (
                      <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <Plus className="w-3 h-3 text-white" strokeWidth={3} />
                    )}
                  </div>

                  <span className="flex-1 text-left font-medium text-green-700 dark:text-green-400 capitalize">
                    {creatingTag ? 'Criando...' : `Criar nova tag: "${searchQuery.trim()}"`}
                  </span>

                  <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-700 dark:text-green-300 rounded-full">
                    {t("tagModal.new")}
                  </span>
                </button>
              )}

              {filteredTags.length === 0 && !canCreateNewTag ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-card-muted dark:bg-neutral-800 rounded-full flex items-center justify-center mb-4">
                    <Search className="w-8 h-8 text-muted-foreground dark:text-neutral-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground dark:text-neutral-300 mb-2">
                    {searchQuery ? 'Nenhuma tag encontrada' : 'Nenhuma tag cadastrada'}
                  </h3>
                  <p className="text-sm text-muted-foreground dark:text-neutral-500 max-w-xs">
                    {searchQuery
                      ? 'Tente buscar com outros termos ou crie uma nova tag'
                      : 'Cadastre tags nas configurações para começar a organizar seus contatos'
                    }
                  </p>
                </div>
              ) : (
            <div className="flex flex-col gap-2">
              {filteredTags.map((tag) => {
                const isSelected = tempSelectedTags.includes(tag.name.toLowerCase());
                const wasOriginallySelected = selectedTags.includes(tag.name.toLowerCase());
                const hasChanged = isSelected !== wasOriginallySelected;

                return (
                  <button
                    key={tag.id}
                    onClick={() => handleToggleTag(tag.name.toLowerCase())}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-lg border transition-all duration-200 group
                      ${isSelected
                        ? 'bg-[#EEF4FF] border-primary/40 hover:bg-[#DBEAFE] dark:bg-blue-500/10 dark:border-blue-500/50 dark:hover:bg-blue-500/20'
                        : 'bg-[#F8FAFC] border-border hover:bg-hover dark:bg-neutral-800/50 dark:border-neutral-700/50 dark:hover:bg-neutral-800 dark:hover:border-neutral-600'
                      }
                      ${hasChanged ? 'ring-2 ring-primary/30 dark:ring-blue-500/30' : ''}
                    `}
                  >
                    <div className={`
                      flex items-center justify-center w-5 h-5 rounded border-2 transition-all duration-200
                      ${isSelected
                        ? 'bg-primary border-primary dark:bg-blue-500 dark:border-blue-500'
                        : 'border-border group-hover:border-muted-foreground dark:border-neutral-600 dark:group-hover:border-neutral-500'
                      }
                    `}>
                      {isSelected && (
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      )}
                    </div>

                    <span className={`
                      flex-1 text-left font-medium capitalize transition-colors duration-200
                      ${isSelected ? 'text-primary dark:text-white' : 'text-foreground dark:text-neutral-300'}
                    `}>
                      {tag.name}
                    </span>

                    {hasChanged && (
                      <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary dark:bg-blue-500/20 dark:text-blue-400 rounded-full">
                        {isSelected ? 'Adicionar' : 'Remover'}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
              )}
            </div>
          )}
        </ScrollArea>

        <div className="px-6 py-4 border-t border-border flex items-center justify-between gap-3 dark:border-neutral-800">
          <div className="flex items-center gap-2">
            {hasChanges && (
              <span className="text-sm text-muted-foreground dark:text-neutral-400">
                {tempSelectedTags.length} {tempSelectedTags.length === 1 ? 'tag selecionada' : 'tags selecionadas'}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={handleCancel}
              disabled={applying}
              className="text-muted-foreground hover:text-foreground hover:bg-hover dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-800"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleApply}
              disabled={!hasChanges || applying}
              className="bg-primary hover:bg-primary-hover text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-white"
            >
              {applying ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t("tagModal.applying")}
                </>
              ) : (
                <>{t("tagModal.apply")}</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TagSelectorModal;
